"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateOutreachAvailability = exports.calculateUserJobAllScheduledCount = exports.calculateOutreachCounts = exports.getOutreachLimitingFactorLabel = exports.getRevivalReasonLabel = exports.revivalReasons = void 0;
const job_stages_1 = require("./job-stages");
const revivalDisqualReasons = [
    'candidate_declined_compensation_issue',
    'candidate_declined_contract_only',
    'candidate_declined_permanent_only',
    'candidate_declined_location_not_a_fit',
    'candidate_declined_not_interested_in_role',
    'candidate_declined_remote_only',
    'candidate_declined_withdrawing',
    'recruiter_declined_added_by_mistake',
    'recruiter_declined_gone_cold',
    'recruiter_declined_not_qualified_on_review',
    'recruiter_declined_visa_issue'
];
exports.revivalReasons = ['never_responded', ...revivalDisqualReasons];
function getRevivalReasonLabel(reason) {
    if (reason === 'never_responded') {
        return 'Never Responded';
    }
    return (0, job_stages_1.getDisqualReasonLabel)(reason, true);
}
exports.getRevivalReasonLabel = getRevivalReasonLabel;
function getOutreachLimitingFactorLabel(limitingFactor) {
    if (limitingFactor === 'available_candidates') {
        return 'Insufficient candidates/search results';
    }
    else if (limitingFactor === 'job_daily_limit') {
        return 'Job daily limit reached';
    }
    else if (limitingFactor === 'user_job_daily_limit') {
        return 'User job daily limit reached';
    }
    else if (limitingFactor === 'email_accounts') {
        return 'Email accounts limit reached';
    }
    else if (limitingFactor === 'user_daily_limit') {
        return 'User daily limit reached';
    }
    else if (limitingFactor === 'sequence_not_found') {
        return 'No outreach sequence found';
    }
    else {
        return 'Unknown';
    }
}
exports.getOutreachLimitingFactorLabel = getOutreachLimitingFactorLabel;
/**
 * Calculate outreach counts for a specific day and job/user
 */
const calculateOutreachCounts = (outreaches, jobId, userId, sendingDayStart, sendingDayEnd) => {
    const jobSentCount = outreaches
        .filter((o) => o.jobId === jobId &&
        o.status === 'complete' &&
        o.scheduledAt >= sendingDayStart &&
        o.scheduledAt <= sendingDayEnd)
        .reduce((sum, o) => o.addedCount + sum, 0);
    const jobScheduledCount = outreaches
        .filter((o) => o.jobId === jobId &&
        (o.status === 'scheduled' || o.status === 'inProgress') &&
        o.scheduledAt >= sendingDayStart &&
        o.scheduledAt <= sendingDayEnd)
        .reduce((sum, o) => o.limit + sum, 0);
    const userSentCount = outreaches
        .filter((o) => o.userId === userId &&
        o.status === 'complete' &&
        o.scheduledAt >= sendingDayStart &&
        o.scheduledAt <= sendingDayEnd)
        .reduce((sum, o) => o.addedCount + sum, 0);
    const userScheduledCount = outreaches
        .filter((o) => o.userId === userId &&
        (o.status === 'scheduled' || o.status === 'inProgress') &&
        o.scheduledAt >= sendingDayStart &&
        o.scheduledAt <= sendingDayEnd)
        .reduce((sum, o) => o.limit + sum, 0);
    const userJobSentCount = outreaches
        .filter((o) => o.jobId === jobId &&
        o.userId === userId &&
        o.status === 'complete' &&
        o.scheduledAt >= sendingDayStart &&
        o.scheduledAt <= sendingDayEnd)
        .reduce((sum, o) => o.addedCount + sum, 0);
    const userJobScheduledCount = outreaches
        .filter((o) => o.jobId === jobId &&
        o.userId === userId &&
        (o.status === 'scheduled' || o.status === 'inProgress') &&
        o.scheduledAt >= sendingDayStart &&
        o.scheduledAt <= sendingDayEnd)
        .reduce((sum, o) => o.limit + sum, 0);
    return {
        jobScheduledCount,
        jobSentCount,
        userJobScheduledCount,
        userJobSentCount,
        userScheduledCount,
        userSentCount
    };
};
exports.calculateOutreachCounts = calculateOutreachCounts;
/**
 * Calculate total scheduled count for job+user across all days (not just sending day)
 */
const calculateUserJobAllScheduledCount = (outreaches, jobId, userId) => {
    return outreaches
        .filter((o) => o.jobId === jobId && o.userId === userId && (o.status === 'scheduled' || o.status === 'inProgress'))
        .reduce((sum, o) => o.limit + sum, 0);
};
exports.calculateUserJobAllScheduledCount = calculateUserJobAllScheduledCount;
/**
 * Validate if outreach can be sent and calculate maximum available slots
 */
const validateOutreachAvailability = (availableCount, counts, limits, userJobAllScheduledCount) => {
    var _a;
    const { jobSentCount, jobScheduledCount, userSentCount, userScheduledCount, userJobSentCount, userJobScheduledCount } = counts;
    const { dailyEmailLimit, dailyPerUserEmailLimit, emailAccountSlots, userDailyLimit } = limits;
    // Calculate available slots for each constraint
    const jobSlotsAvailable = dailyEmailLimit - jobSentCount - jobScheduledCount;
    const userJobSlotsAvailable = dailyPerUserEmailLimit - userJobSentCount - userJobScheduledCount;
    const emailAccountSlotsAvailable = emailAccountSlots - userSentCount - userScheduledCount;
    const userDailyLimitSlotsAvailable = userDailyLimit - userSentCount - userScheduledCount;
    // Available to send is available count minus what is already scheduled (across all days)
    // Sent is already accounted for in availableCount
    const availableToSendCount = availableCount - userJobAllScheduledCount;
    // Find the limiting factor and max available
    const constraints = [
        { value: availableToSendCount, factor: 'available_candidates', name: 'Available candidates' },
        { value: jobSlotsAvailable, factor: 'job_daily_limit', name: 'Job daily limit' },
        { value: userJobSlotsAvailable, factor: 'user_job_daily_limit', name: 'User job daily limit' },
        { value: emailAccountSlotsAvailable, factor: 'email_accounts', name: 'Email account slots' },
        { value: userDailyLimitSlotsAvailable, factor: 'user_daily_limit', name: 'User daily limit' }
    ];
    const maxAvailable = Math.max(0, Math.min(...constraints.map((c) => c.value)));
    const limitingConstraint = constraints.find((c) => c.value === maxAvailable && c.value >= 0);
    return {
        availableToSendCount,
        canSend: maxAvailable > 0,
        emailAccountSlotsAvailable,
        jobSlotsAvailable,
        limitingFactor: (_a = limitingConstraint === null || limitingConstraint === void 0 ? void 0 : limitingConstraint.factor) !== null && _a !== void 0 ? _a : null,
        maxAvailable,
        userDailyLimitSlotsAvailable,
        userJobSlotsAvailable
    };
};
exports.validateOutreachAvailability = validateOutreachAvailability;
//# sourceMappingURL=scheduled-outreach.js.map